import { useEffect, useMemo, useReducer, useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { buildScenarioDocument, documentFilename } from "@shared/roi/document";
import { computeProjection } from "@shared/roi/engine";
import { toScenarioModel, type ScenarioDetail } from "@shared/schemas/scenario";
import type { ProjectionWarning } from "@shared/roi/types";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { ShareDialog } from "@client/components/ShareDialog";
import { OutputsColumn } from "@client/components/outputs/OutputsColumn";
import { ConflictDialog } from "@client/components/editor/ConflictDialog";
import { EditorIntro } from "@client/components/editor/EditorIntro";
import { EditorPanelDialog } from "@client/components/editor/EditorPanelDialog";
import { EditorToolbar, type EditorPanelKey } from "@client/components/editor/EditorToolbar";
import { KpiStrip } from "@client/components/editor/KpiStrip";
import { SaveStatus } from "@client/components/editor/SaveStatus";
import { WarningList } from "@client/components/editor/WarningList";
import { useProduct } from "@client/hooks/useProducts";
import { useScenario, useDuplicateScenario } from "@client/hooks/useScenarios";
import { useScenarioSave } from "@client/hooks/useScenarioSave";
import { formatDateTime, formatRelative } from "@client/lib/datetime";
import { buttonClass } from "@client/lib/buttonStyles";
import { clearDraftMirror, readDraftMirror } from "@client/lib/draftMirror";
import { describeError } from "@client/lib/errorMessage";
import { downloadJson } from "@client/lib/fileTransfer";
import { draftReducer, type EditorModel } from "@client/lib/scenarioDraft";
import { sameShape } from "@client/lib/stableJson";
import { saveScenarioModel } from "@client/lib/scenariosApi";
import { warningPanel, warningTarget } from "@client/lib/warningLinks";

const route = getRouteApi("/_authed/products/$slug/scenarios/$scenarioId");

/**
 * The scenario editor — phase 6 of docs/10-implementation-plan.md, and the
 * generalised descendant of the ZNMS `RoiPage`.
 *
 * An icon rail on the left opens the inputs in a dialog; the page itself is the
 * KPI strip, the warnings, the charts and the year-by-year table, at the full
 * width of the shell. The inputs used to be a 460px column beside the outputs,
 * which cost the charts a third of the page and the reader several screens of
 * scrolling to compare a number with the figure it moved.
 *
 * A dialog rather than a route or a drawer because the projection is synchronous
 * and local: the KPI strip stays visible behind the overlay and still moves on
 * every keystroke, so a price can be changed while its consequence is on screen.
 *
 * The draft is local and synchronous — a `useReducer` seeded from the query,
 * projected through `useMemo` — so a slider moves the figures without a round
 * trip. The network is involved on save and nowhere else. See
 * docs/08-frontend.md#state-management.
 */
export function ScenarioEditorPage() {
  const { slug, scenarioId } = route.useParams();
  const scenario = useScenario(scenarioId);

  if (scenario.isPending) return <LoadingBlock rows={6} label="Loading scenario" />;
  if (scenario.isError) {
    return <ErrorNotice error={scenario.error} onRetry={() => void scenario.refetch()} />;
  }

  // Keyed on the id so moving between scenarios starts a fresh draft rather than
  // carrying one scenario's unsaved edits into another's reducer.
  return <ScenarioEditor key={scenario.data.id} detail={scenario.data} slug={slug} />;
}

function ScenarioEditor({ detail, slug }: { detail: ScenarioDetail; slug: string }) {
  const navigate = useNavigate();
  const canEdit = detail.abilities.canEdit;

  // Seeded once. Later refetches of this query — a window refocus, a sibling
  // mutation's invalidation — must not overwrite what someone is typing; a save
  // that has actually moved the version says so through the conflict dialog.
  const [draft, dispatch] = useReducer(draftReducer, detail, (loaded) => ({
    model: loaded.model,
    version: loaded.version,
    dirty: false,
  }));

  // Converted once and shared: the engine and the charts read the same object,
  // so a package colour or a line item's group cannot differ between the figures
  // and the bars drawn from them.
  const model = useMemo(
    () => toScenarioModel(draft.model, draft.model.currencyCode),
    [draft.model],
  );
  const projection = useMemo(() => computeProjection(model), [model]);

  // Only for the product's *name*, which the scenario payload does not carry and
  // the exported document wants. It is nearly always already in the cache — this
  // page is reached from the product — and the export falls back to the slug
  // while a deep link is still resolving it.
  const product = useProduct(slug);
  const productName = product.data?.name ?? slug;

  const save = useScenarioSave({ scenarioId: detail.id, draft, dispatch, enabled: canEdit });
  const duplicate = useDuplicateScenario();
  const [sharing, setSharing] = useState(false);
  const [panel, setPanel] = useState<EditorPanelKey | null>(null);
  // Set together with `panel` by a warning's "Go to it", and consumed by the
  // effect below once the dialog holding the anchor has actually mounted.
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [forking, setForking] = useState(false);
  const [forkError, setForkError] = useState<unknown>(null);

  // The recovery mirror, read once on mount. Offered only when it actually
  // differs from what the server holds — otherwise every reload of a
  // successfully saved scenario would ask a question with no consequence.
  const [recovery, setRecovery] = useState(() => {
    if (!canEdit) return null;
    const mirror = readDraftMirror(detail.id);
    if (!mirror) return null;
    // Compared by shape, not by bytes: the stored copy and the one zod rebuilt
    // from the response order their keys differently and are the same model.
    return sameShape(mirror.model, detail.model) ? null : mirror;
  });

  const dismissRecovery = () => {
    clearDraftMirror(detail.id);
    setRecovery(null);
  };

  /**
   * Send a warning to the field that caused it.
   *
   * The field is inside a dialog that is not mounted yet, so this is two steps:
   * open the right one, then scroll to the anchor on the tick after it mounts.
   * The dialog is portalled and rendered synchronously with the state change, so
   * one effect pass is enough — no timer, no animation frame.
   */
  const goToWarning = (warning: ProjectionWarning) => {
    const next = warningPanel(warning);
    if (next === null) return;
    setPanel(next);
    setPendingAnchor(warningTarget(warning));
  };

  useEffect(() => {
    if (panel === null || pendingAnchor === null) return;
    // Optional call: `scrollIntoView` is missing in jsdom and in a few older
    // mobile browsers, and landing on the right dialog without the scroll is a
    // far better outcome than a TypeError out of an effect.
    document.getElementById(pendingAnchor)?.scrollIntoView?.({ block: "center" });
    setPendingAnchor(null);
  }, [panel, pendingAnchor]);

  /**
   * Export what is on screen, not what the server holds.
   *
   * The draft is the thing the person is looking at, and a download that quietly
   * omitted the last ten minutes of unsaved edits would be worse than no
   * download. It is built with the same `buildScenarioDocument` the API uses, so
   * a file saved from here and one fetched from `/export` are the same document.
   */
  const exportDraft = () => {
    const document = buildScenarioDocument({
      product: { name: productName, currencyCode: draft.model.currencyCode },
      scenario: {
        name: detail.name,
        description: detail.description,
        id: detail.id,
        version: draft.version,
      },
      model,
    });
    downloadJson(documentFilename(productName, detail.name), document);
  };

  /** "Open both": my work becomes a scenario of my own, theirs is left alone. */
  const openBoth = async (mine: EditorModel) => {
    setForking(true);
    setForkError(null);
    try {
      const copy = await duplicate.mutateAsync({ id: detail.id, name: `${detail.name} (mine)` });
      await saveScenarioModel(copy.id, { version: copy.version, model: mine });
      clearDraftMirror(detail.id);
      save.clearConflict();
      await navigate({
        to: "/products/$slug/scenarios/$scenarioId",
        params: { slug, scenarioId: copy.id },
      });
    } catch (cause) {
      setForkError(cause);
    } finally {
      setForking(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Link
            to="/products/$slug"
            params={{ slug }}
            className="text-sm font-semibold text-azure-deep hover:underline"
          >
            ← Back to the product
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-ink">{detail.name}</h1>
            {detail.isBaseline && <Badge kind="info">Baseline</Badge>}
            {detail.archivedAt && <Badge kind="warning">Archived</Badge>}
          </div>
          <p className="text-sm text-ink-soft">
            {detail.author.fullName} · version {draft.version} · last saved{" "}
            {formatDateTime(detail.updatedAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SaveStatus save={save} canEdit={canEdit} />
          <Link
            to="/products/$slug/scenarios/$scenarioId/versions"
            params={{ slug, scenarioId: detail.id }}
            className={buttonClass("ghost", "sm")}
          >
            Versions
          </Link>
          <Button variant="ghost" size="sm" onClick={exportDraft}>
            Export
          </Button>
          {detail.abilities.canShare && (
            <Button variant="ghost" size="sm" onClick={() => setSharing(true)}>
              Share
            </Button>
          )}
        </div>
      </header>

      {!canEdit && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-chip-bg px-4 py-3"
        >
          <p className="min-w-[16rem] flex-1 text-base text-ink">
            <span className="font-semibold">You are reading this scenario, not editing it.</span>{" "}
            {detail.archivedAt
              ? "It is archived, so nobody can edit it until it is restored."
              : "It belongs to somebody else and was shared with you to view. Take a copy and the copy is yours to change."}
          </p>
          <Button
            size="sm"
            disabled={duplicate.isPending}
            onClick={() =>
              duplicate.mutate(
                { id: detail.id },
                {
                  onSuccess: (copy) =>
                    void navigate({
                      to: "/products/$slug/scenarios/$scenarioId",
                      params: { slug, scenarioId: copy.id },
                    }),
                },
              )
            }
          >
            {duplicate.isPending ? "Copying…" : "Duplicate to edit"}
          </Button>
        </div>
      )}

      {duplicate.isError && !forking && <ErrorNotice error={duplicate.error} />}

      {recovery && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
          style={{ borderColor: "var(--chart-warn)" }}
        >
          <p className="min-w-[16rem] flex-1 text-base text-ink">
            <span className="font-semibold">Unsaved changes were found on this device</span> from{" "}
            {formatRelative(recovery.savedAt)}, against version {recovery.version}. They never
            reached the server.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                dispatch({ type: "restored", model: recovery.model, version: draft.version });
                setRecovery(null);
              }}
            >
              Restore them
            </Button>
            <Button variant="ghost" size="sm" onClick={dismissRecovery}>
              Discard
            </Button>
          </div>
        </div>
      )}

      {/* Below the two notices above, which are about *this* scenario and can be
          acted on; the explainer is about the screen and can wait its turn. */}
      <EditorIntro canEdit={canEdit} />

      <KpiStrip
        projection={projection}
        currencyCode={draft.model.currencyCode}
        horizonYears={draft.model.horizonYears}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <EditorToolbar
          open={panel}
          onOpen={(key) => setPanel((current) => (current === key ? null : key))}
          counts={{
            packages: draft.model.packages.length,
            lines: draft.model.lineItems.length,
            years: draft.model.years.length,
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <WarningList warnings={projection.warnings} onGoTo={goToWarning} />

          <OutputsColumn
            model={model}
            projection={projection}
            currencyCode={draft.model.currencyCode}
          />
        </div>
      </div>

      <EditorPanelDialog
        panel={panel}
        onClose={() => setPanel(null)}
        model={draft.model}
        dispatch={dispatch}
        disabled={!canEdit}
      />

      <ShareDialog
        scenario={sharing ? detail : null}
        open={sharing}
        onClose={() => setSharing(false)}
      />

      <ConflictDialog
        conflict={save.conflict}
        mine={draft.model}
        busy={save.status === "saving" || forking}
        error={forkError ?? (save.status === "error" ? save.error : null)}
        onKeepMine={save.keepMine}
        onTakeTheirs={() => {
          const theirs = save.conflict?.current;
          if (!theirs) return;
          dispatch({ type: "loaded", model: theirs.model, version: theirs.version });
          clearDraftMirror(detail.id);
          save.clearConflict();
        }}
        onOpenBoth={() => void openBoth(draft.model)}
        onCancel={save.clearConflict}
      />

      {forkError !== null && !save.conflict && (
        <p role="alert" className="text-sm font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(forkError)}
        </p>
      )}
    </div>
  );
}
